export const aboutPageTranslations = {
  en: {
    badge: 'United for Mental Well-Being',

    whoWeAre: {
      title: 'Who We Are',
      body: 'TogetherWe is a community-powered effort to strengthen connection and mental well-being for every resident in Blaine County — now and for generations to come.',
    },

    intro: {
      p1: 'We help many organizations work together as one coordinated system so people in our community can find support more easily and get help sooner.',
      p2: 'By organizing and aligning community resources, identifying gaps, and amplifying the work of local partners, TogetherWe makes it easier for residents to find connection and support in one trusted place.',
      viewOurWork: 'View Our Work',
    },

    whatWeDo: {
      title: 'What We Do',
      subtitle:
        'While partners deliver programs and services across the community, TogetherWe focuses on the work that helps the entire system function better.',
      items: [
        {
          title: 'Bring Partners Together',
          description:
            'We convene organizations across Blaine County to align around shared priorities, reduce duplication, and strengthen collaboration.',
        },
        {
          title: 'Use Data to Guide Community Action',
          description:
            'We gather and analyze local data to understand community needs, identify gaps, and track progress toward improving mental well-being.',
        },
        {
          title: 'Direct Resources Where They Matter Most',
          description:
            'We help align funding, grants, and community investments so resources support the areas with the greatest need and potential impact.',
        },
        {
          title: 'Make Support Easier to Find',
          description:
            'We coordinate shared communication and tools that help residents discover programs, events, and services across the community.',
        },
        {
          title: 'Lead Priority Initiatives',
          description:
            'When critical efforts need neutral leadership, incubation, or coordination, TogetherWe helps move them forward so important work does not stall.',
        },
      ],
    },

    whyItMatters: {
      title: 'Why This Work Matters',
      p1: 'When our community asks, \u201cDo we have the right resources here?\u201d TogetherWe helps answer that question with data, coordination, and action.',
      p2: 'When critical gaps emerge, we work alongside partners to incubate, stabilize, or launch solutions \u2014 collaboratively and with a plan for long-term sustainability.',
    },

    systemSection: {
      lead: 'TogetherWe exists to do what no single organization can do alone:',
      bullets: [
        'See the full system across prevention, care, and crisis support',
        'Identify gaps in access and capacity',
        'Align partners, funding, and strategy',
        'Catalyze solutions when services do not yet exist',
      ],
      closing:
        'This work is rooted in the belief that lasting change happens through shared leadership, trust, and long-term commitment.',
    },

    whatWeBelieve: {
      title: 'What We Believe:',
      items: [
        'Lasting change happens when communities work together',
        'Mental well-being resources should be clear and easy to access',
        'Connection and belonging are essential to a healthy community',
        'Every person deserves to feel safe, valued, and supported',
      ],
    },

    ourOrigins: {
      title: 'Our Origins',
      paragraphs: [
        'What began in 2023 as the St. Luke’s Wood River Foundation’s Mental Well-Being Initiative has grown into TogetherWe — an independent nonprofit shaped by community listening, collaboration, and a shared commitment to strengthening mental well-being across Blaine County.',
        'The initiative emerged from a clear need: many organizations were doing important work, but efforts were often fragmented and difficult for residents to navigate.',
        'TogetherWe was created to help organize, align, and strengthen this work across the community.',
        'Today, TogetherWe connects a growing network of more than 60 partners across healthcare, education, government, nonprofits, business, and faith communities — working together to improve connection, access to care, and crisis response across the valley.',
        'TogetherWe does not replace services or compete with providers. Instead, we help ensure the entire system works better for everyone.',
      ],
    },

    nav: {
      ourTeam: 'Our Team',
      ourPartners: 'Our Partners',
    },

    team: {
      label: 'Leadership & Guidance',
      body: 'Our team is made up of staff and board members who provide steady leadership, shared accountability, and guidance toward community impact.',
      ourStaff: 'Our Staff',
      ourBoard: 'Our Board',
      readBio: 'Read Bio',
      navLeft: 'Our Partners',
      navRight: 'About TogetherWe',
    },

    partners: {
      title: 'A Collective-Impact Approach',
      ourPartners: 'Our Partners',
      p1: 'Partners come together to do what no single organization can do alone, aligning around shared goals, common measures, and coordinated action \u2014 so progress is cumulative and lasting.',
      p2: 'TogetherWe is a shared effort grounded in the belief that care, connection, and support should be within reach \u2014 for everyone.',
      strategicLabel: 'Strategic Partners',
      alignedLabel: 'Aligned Partners',
      awarenessLabel: 'Awareness Partners',
      collectiveImpact: {
        title: 'Learn more about Collective Impact',
        learnMore: 'Learn More',
      },
      navLeft: 'About TogetherWe',
      navRight: 'Our Team',
    },
  },

  es: {
    badge: 'Unidos por el Bienestar Mental',

    whoWeAre: {
      title: 'Quiénes Somos',
      body: 'TogetherWe es un esfuerzo impulsado por la comunidad para fortalecer la conexión y el bienestar mental de cada residente del condado de Blaine, ahora y para las generaciones futuras.',
    },

    intro: {
      p1: 'Ayudamos a muchas organizaciones a trabajar juntas como un sistema coordinado para que las personas de nuestra comunidad puedan encontrar apoyo más fácilmente y recibir ayuda antes.',
      p2: 'Al organizar y alinear los recursos comunitarios, identificar brechas y amplificar el trabajo de los socios locales, TogetherWe facilita que los residentes encuentren conexión y apoyo en un lugar de confianza.',
      viewOurWork: 'Ver Nuestro Trabajo',
    },

    whatWeDo: {
      title: 'Lo Que Hacemos',
      subtitle:
        'Mientras los socios ofrecen programas y servicios en toda la comunidad, TogetherWe se enfoca en el trabajo que ayuda a que todo el sistema funcione mejor.',
      items: [
        {
          title: 'Unir a los Socios',
          description:
            'Convocamos organizaciones en el condado de Blaine para alinearse en torno a prioridades compartidas, reducir la duplicación y fortalecer la colaboración.',
        },
        {
          title: 'Usar Datos para Guiar la Acción Comunitaria',
          description:
            'Recopilamos y analizamos datos locales para comprender las necesidades de la comunidad, identificar brechas y hacer seguimiento del progreso hacia la mejora del bienestar mental.',
        },
        {
          title: 'Dirigir Recursos Donde Más se Necesitan',
          description:
            'Ayudamos a alinear financiamiento, donaciones e inversiones comunitarias para que los recursos apoyen las áreas con mayor necesidad e impacto potencial.',
        },
        {
          title: 'Facilitar el Acceso al Apoyo',
          description:
            'Coordinamos comunicaciones y herramientas compartidas que ayudan a los residentes a descubrir programas, eventos y servicios en toda la comunidad.',
        },
        {
          title: 'Liderar Iniciativas Prioritarias',
          description:
            'Cuando los esfuerzos críticos necesitan un liderazgo neutral, incubación o coordinación, TogetherWe los impulsa para que el trabajo importante no se detenga.',
        },
      ],
    },

    whyItMatters: {
      title: 'Por Qué Este Trabajo Importa',
      p1: 'Cuando nuestra comunidad pregunta: \u201c\u00bfTenemos los recursos adecuados aquí?\u201d TogetherWe ayuda a responder esa pregunta con datos, coordinación y acción.',
      p2: 'Cuando surgen brechas críticas, trabajamos junto a los socios para incubar, estabilizar o lanzar soluciones, de forma colaborativa y con un plan de sostenibilidad a largo plazo.',
    },

    systemSection: {
      lead: 'TogetherWe existe para hacer lo que ninguna organización puede hacer sola:',
      bullets: [
        'Ver el sistema completo a través de la prevención, la atención y el apoyo en crisis',
        'Identificar brechas en el acceso y la capacidad',
        'Alinear socios, financiamiento y estrategia',
        'Catalizar soluciones cuando los servicios aún no existen',
      ],
      closing:
        'Este trabajo está arraigado en la creencia de que el cambio duradero ocurre a través del liderazgo compartido, la confianza y el compromiso a largo plazo.',
    },

    whatWeBelieve: {
      title: 'En lo que Creemos:',
      items: [
        'El cambio duradero ocurre cuando las comunidades trabajan juntas',
        'Los recursos de bienestar mental deben ser claros y fáciles de acceder',
        'La conexión y el sentido de pertenencia son esenciales para una comunidad saludable',
        'Cada persona merece sentirse segura, valorada y apoyada',
      ],
    },

    ourOrigins: {
      title: 'Nuestros Orígenes',
      paragraphs: [
        'Lo que comenzó en 2023 como la Iniciativa de Bienestar Mental de la Fundación St. Luke’s Wood River se ha convertido en TogetherWe, una organización sin fines de lucro independiente moldeada por la escucha comunitaria, la colaboración y un compromiso compartido para fortalecer el bienestar mental en todo el condado de Blaine.',
        'La iniciativa surgió de una necesidad clara: muchas organizaciones realizaban un trabajo importante, pero los esfuerzos a menudo estaban fragmentados y eran difíciles de encontrar y usar para los residentes.',
        'TogetherWe se creó para ayudar a organizar, alinear y fortalecer este trabajo en toda la comunidad.',
        'Hoy, TogetherWe conecta una red creciente de más de 60 socios en los sectores de salud, educación, gobierno, organizaciones sin fines de lucro, empresas y comunidades de fe, trabajando juntos para mejorar la conexión, el acceso a la atención y la respuesta a las crisis en todo el valle.',
        'TogetherWe no reemplaza los servicios ni compite con los proveedores. En cambio, ayudamos a garantizar que todo el sistema funcione mejor para todos.',
      ],
    },

    nav: {
      ourTeam: 'Nuestro Equipo',
      ourPartners: 'Nuestros Socios',
    },

    team: {
      label: 'Liderazgo y Orientación',
      body: 'Nuestro equipo está formado por personal y miembros de la junta que brindan un liderazgo constante, responsabilidad compartida y orientación hacia el impacto comunitario.',
      ourStaff: 'Nuestro Personal',
      ourBoard: 'Nuestra Junta',
      readBio: 'Leer Bio',
      navLeft: 'Nuestros Socios',
      navRight: 'Sobre TogetherWe',
    },

    partners: {
      title: 'Un Enfoque de Impacto Colectivo',
      ourPartners: 'Nuestros Socios',
      p1: 'Los socios se unen para hacer lo que ninguna organización puede hacer sola, alineándose en torno a objetivos compartidos, medidas comunes y acción coordinada, para que el progreso sea acumulativo y duradero.',
      p2: 'TogetherWe es un esfuerzo compartido basado en la creencia de que el cuidado, la conexión y el apoyo deben estar al alcance de todos.',
      strategicLabel: 'Socios Estratégicos',
      alignedLabel: 'Socios Aliados',
      awarenessLabel: 'Socios de Difusión',
      collectiveImpact: {
        title: 'Conoce más sobre el Impacto Colectivo',
        learnMore: 'Conocer Más',
      },
      navLeft: 'Sobre TogetherWe',
      navRight: 'Nuestro Equipo',
    },
  },
} as const;

export type AboutLang = keyof typeof aboutPageTranslations;
